import React, { useMemo } from "react";
import { Chart } from "primereact/chart";
import type { ChartData, ChartOptions } from "chart.js";
import type { MonthKey } from "../../../types/targetGroupAnalytics";
import {
  TEXT_COLOR,
  TEXT_COLOR_SECONDARY,
  SURFACE_BORDER,
} from "../../../styles/theme_constants";

const MONTH_ABBREVIATIONS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Inlined when this component was promoted out of TargetGroups/Observability so a shared graph does not
 * import from a feature folder. The feature keeps its own `monthLabel` for its other uses.
 */
const monthLabel = (m: MonthKey): string =>
  `${MONTH_ABBREVIATIONS[m.month - 1]} '${String(m.year).slice(-2)}`;

export interface SeriesDef {
  label: string;
  color: string;
  data: number[];
}

interface MonthlyTimeSeriesProps {
  title: string;
  months: MonthKey[];
  /** Per-month finality; a trailing `false` marks the in-progress month (FR-012). */
  finals: boolean[];
  series: SeriesDef[];
}

/**
 * Monthly line time-series.
 *
 * Promoted from `TargetGroups/Observability` when station performance (CP-388) became the second
 * consumer: the codebase convention is to share only once something is used by more than one feature.
 * Behaviour is unchanged.
 * Self-contained (LinearChart truncates
 * labels to 3 chars and can't mark the in-progress month). The current month
 * is rendered with a dashed trailing segment + an "in progress" caption.
 */
const MonthlyTimeSeries: React.FC<MonthlyTimeSeriesProps> = ({ title, months, finals, series }) => {
  const lastIdx = months.length - 1;
  const currentInProgress = lastIdx >= 0 && finals[lastIdx] === false;

  const data = useMemo<ChartData<"line">>(() => {
    return {
      labels: months.map(monthLabel),
      datasets: series.map((s) => ({
        label: s.label,
        data: s.data,
        borderColor: s.color,
        backgroundColor: s.color,
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        segment: {
          borderDash: (ctx: any) =>
            currentInProgress && ctx.p1DataIndex === lastIdx ? [6, 6] : undefined,
        },
      })),
    };
  }, [months, series, currentInProgress, lastIdx]);

  const options = useMemo<ChartOptions<"line">>(
    () => ({
      animation: { duration: 0 },
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          display: series.length > 1,
          labels: { color: TEXT_COLOR, usePointStyle: true },
        },
      },
      scales: {
        x: { ticks: { color: TEXT_COLOR_SECONDARY }, grid: { color: SURFACE_BORDER } },
        y: {
          beginAtZero: true,
          ticks: { color: TEXT_COLOR_SECONDARY },
          grid: { color: SURFACE_BORDER },
        },
      },
    }),
    [series.length],
  );

  return (
    <div className="px-2 py-2" data-testid="monthly-time-series">
      <div className="flex align-items-center justify-content-between mb-2 gap-2">
        <span className="font-semibold text-800 text-sm">{title}</span>
        {currentInProgress && (
          <span className="text-xs text-color-secondary white-space-nowrap">
            <i className="pi pi-circle-fill mr-1" style={{ fontSize: "0.45rem" }} />
            current month in progress
          </span>
        )}
      </div>
      <Chart type="line" data={data} options={options} height="240px" className="w-full" />
    </div>
  );
};

export default MonthlyTimeSeries;
